import { isNullOrEmpty } from ".";

export function showElement(element: HTMLElement | Element, cssClass?: string, displayClass?: string): void {
  const ele = element as HTMLElement;
  if (!ele) {
    return;
  }

  cssClass = cssClass || "show";

  ele.classList.add(cssClass);
  ele.style.display = displayClass || "";
}

export function hideElement(element: HTMLElement | Element, cssClass?: string): void {
  const ele = element as HTMLElement;
  if (!ele) {
    return;
  }

  cssClass = cssClass || "hide";

  ele.classList.remove(cssClass);
  ele.style.display = "none";
}

export function addEventToElements(
  selector: string,
  eventName: keyof GlobalEventHandlersEventMap,
  handler: EventListenerOrEventListenerObject
): void {
  const elements = queryDocument(selector);
  elements.forEach((element) => {
    element.addEventListener(eventName, handler);
  });
}

export function addClickEventToElements(selector: string, handler: EventListenerOrEventListenerObject): void {
  addEventToElements(selector, "click", handler);
}

export function getEventTarget<T extends Element>(event: Event): T {
  return event.target as T;
}

export function queryDocument<T extends Element>(selector: string): T[] {
  return Array.from(document.querySelectorAll<T>(selector));
}

export function queryFirstDocument<T extends Element>(selector: string): T | undefined {
  var elements = queryDocument<T>(selector);

  if (!elements || elements.length === 0) {
    return undefined;
  }

  return elements[0];
}

export function query<T extends Element>(element: Element, selector: string): T[] {
  return Array.from(element.querySelectorAll<T>(selector));
}

export function queryFirst<T extends Element>(element: Element, selector: string): T {
  const items = query<T>(element, selector);

  if (!items || items.length === 0) {
    return undefined;
  }

  return items[0];
}

export function attribute(element: Element, key: string): string {
  return element?.getAttribute(key);
}

export function attributeAsInt(element: Element, key: string, defaultValue: number = 0): number {
  const value = element?.getAttribute(key);
  if (isNullOrEmpty(value) || Number.isNaN(value)) {
    return defaultValue;
  }

  return parseInt(value);
}

export function attributeAsFloat(element: Element, key: string, defaultValue: number = 0): number {
  const value = element?.getAttribute(key);
  if (isNullOrEmpty(value) || Number.isNaN(value)) {
    return defaultValue;
  }

  return parseFloat(value);
}

export function emptyChildren(element: Element): void {
  while (element.lastChild) {
    element.removeChild(element.lastChild);
  }
}

export function querySiblings<TSibling extends Element>(source: Element, selector: string): TSibling[] {
  if (!source.parentElement) {
    return [];
  }

  return query(source.parentElement, selector);
}

export function queryFirstSibling<TSibling extends Element>(source: Element, selector: string): TSibling | undefined {
  const siblings = querySiblings<TSibling>(source, selector);
  if (!siblings || siblings.length === 0) {
    return undefined;
  }

  return siblings[0];
}

export function hasAnyElements(selector: string): boolean {
  const siblings = querySiblings(document.body, selector);
  return siblings && siblings.length > 0;
}

export function getEventTargetAttribute(event: Event, attributeName: string): string {
  return attribute(getEventTarget<HTMLElement>(event), attributeName);
}

export function elementWithinRoot(root: HTMLElement, element: HTMLElement): boolean {
  let current = element;

  while (current !== undefined) {
    if (current === root) {
      return true;
    } else if (current === document.body) {
      return false;
    }

    current = current.parentElement;
  }

  return false;
}

export function getSiblings<TSibling extends Element>(element: Element): Element[] {
  let siblings: TSibling[] = [];

  if (!element.parentNode) {
    return siblings;
  }

  let sibling = element.parentNode.firstChild as TSibling;

  while (sibling) {
    if (sibling.nodeType === 1 && sibling !== element) {
      siblings.push(sibling);
    }
    sibling = sibling.nextSibling as TSibling;
  }
  return siblings;
}

export function removeClass(elements: Element[], className: string) {
  if (elements && elements.length > 0) {
    elements.forEach((element) => {
      element.classList.remove(className);
    });
  }
}

export function getParentElement(element: Element, selector: string): HTMLElement | null {
  let currentElement = element.parentElement;

  while (currentElement) {
    if (currentElement.matches(selector)) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }

  return null;
}